import { useTranslation } from 'react-i18next';
import type { Schedule } from '@ghostscreen/contracts';
import { daypartColorFor } from '../../lib/schedule/daypartPalette';
import type { ScheduleConflict } from '../../lib/schedule/findScheduleConflicts';
import {
  formatCountdown,
  msUntilWindowEnd,
  nextOccurrenceMs,
} from '../../lib/schedule/formatCountdown';
import { formatLocalWindow } from '../../lib/schedule/formatLocalWindow';
import {
  isWindowActiveAtMinutes,
  localTimeToMinutes,
} from '../../lib/schedule/scheduleWindow';

type ScheduleStatusSidebarProps = {
  schedules: Schedule[];
  conflicts: ScheduleConflict[];
  now: Date;
  groupMembersById: Map<string, string[]>;
};

/**
 * On air / up next / conflicts — Ghost Screen Schedule.dc.html.
 */
export function ScheduleStatusSidebar({
  schedules,
  conflicts,
  now,
  groupMembersById,
}: ScheduleStatusSidebarProps) {
  const { t } = useTranslation('schedule');
  const minutes = localTimeToMinutes(
    `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
  );
  const active = [...schedules]
    .filter((schedule) =>
      isWindowActiveAtMinutes(
        schedule.startLocalTime,
        schedule.endLocalTime,
        schedule.daysOfWeek,
        now.getDay(),
        minutes,
      ),
    )
    .sort((a, b) => b.priority - a.priority);

  const onAir = active[0] ?? null;
  const onAirScreens = onAir ? countScreensForSchedule(onAir, groupMembersById) : 0;

  const upcoming = [...schedules]
    .map((schedule) => {
      const ms = nextOccurrenceMs(schedule.startLocalTime, schedule.daysOfWeek, now);
      return ms == null ? null : { schedule, ms };
    })
    .filter((item): item is { schedule: Schedule; ms: number } => item != null)
    .filter((item) => !active.some((schedule) => schedule.id === item.schedule.id))
    .sort((a, b) => a.ms - b.ms)
    .slice(0, 3);

  const overlapConflicts = conflicts.filter((item) => item.kind === 'overlap');
  const uncovered = conflicts.filter((item) => item.kind === 'uncovered');

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <section
        className="flex flex-col gap-4 rounded-[14px] border px-[22px] py-5"
        style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
      >
        <span className="text-base font-bold" style={{ color: 'var(--gs-ink)' }}>
          {t('status.onAirTitle')}
        </span>

        {onAir ? (
          <span
            className="flex items-center gap-3 rounded-[11px] border px-4 py-3.5"
            style={{
              background: 'var(--gs-panel2)',
              borderColor: 'var(--gs-line)',
            }}
          >
            <span
              className="h-[9px] w-[9px] shrink-0 rounded-full"
              style={{
                background: 'var(--gs-ok)',
                animation: 'gs-pulse 2.4s ease-in-out infinite',
              }}
              aria-hidden
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[15px] font-bold" style={{ color: 'var(--gs-ink)' }}>
                {onAir.name?.trim() || t('untitledDaypart')}
              </span>
              <span className="text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
                {t('status.onAirMeta', {
                  window: formatLocalWindow(onAir.startLocalTime, onAir.endLocalTime, 'range'),
                  count: onAirScreens,
                  countdown: formatCountdown(
                    msUntilWindowEnd(onAir.startLocalTime, onAir.endLocalTime, now),
                    'ends in',
                  ),
                })}
              </span>
            </span>
          </span>
        ) : (
          <p className="m-0 text-sm" style={{ color: 'var(--gs-muted)' }}>
            {t('status.noOverride')}
          </p>
        )}

        <span className="flex flex-col gap-3">
          <span
            className="text-[12.5px] font-bold uppercase tracking-[0.14em]"
            style={{ color: 'var(--gs-muted)' }}
          >
            {t('status.upNext')}
          </span>
          {upcoming.length === 0 ? (
            <span className="text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
              {t('status.nothingSoon')}
            </span>
          ) : (
            upcoming.map(({ schedule, ms }) => {
              const color = daypartColorFor(schedule.id);
              return (
                <span key={schedule.id} className="flex items-center gap-[11px]">
                  <span
                    className="h-[9px] w-[9px] shrink-0 rounded-[3px]"
                    style={{ background: color.bar }}
                    aria-hidden
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-[13.5px] font-semibold"
                    style={{ color: 'var(--gs-ink)' }}
                  >
                    {schedule.name?.trim() || t('untitled')}
                  </span>
                  <span className="shrink-0 text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
                    {formatCountdown(ms, 'in')}
                  </span>
                </span>
              );
            })
          )}
        </span>
      </section>

      <section
        className="flex flex-col gap-3.5 rounded-[14px] border px-[22px] py-5"
        style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
      >
        <span className="text-base font-bold" style={{ color: 'var(--gs-ink)' }}>
          {t('conflicts.title')}
        </span>

        {overlapConflicts.length === 0 && uncovered.length === 0 ? (
          <span
            className="rounded-[10px] border border-s-[3px] px-[15px] py-[13px] text-[13.5px]"
            style={{
              background: 'var(--gs-panel2)',
              borderColor: 'var(--gs-line)',
              borderInlineStartColor: 'var(--gs-ok)',
              color: 'var(--gs-muted)',
            }}
          >
            {t('conflicts.none')}
          </span>
        ) : null}

        {overlapConflicts.slice(0, 4).map((conflict) => (
          <ConflictCard
            key={conflict.id}
            title={t('conflicts.overlapTitle', {
              winner: conflict.winnerName,
              loser: conflict.loserName,
            })}
            detail={conflict.detail}
            accent="var(--gs-warn)"
          />
        ))}
        {uncovered.slice(0, 3).map((conflict) => (
          <ConflictCard
            key={conflict.id}
            title={t('conflicts.uncoveredTitle', { screen: conflict.screenName })}
            detail={conflict.detail}
            accent="var(--gs-bad)"
          />
        ))}

        <span
          className="text-[12.5px] leading-normal"
          style={{ color: 'var(--gs-muted)' }}
        >
          {t('conflicts.footnote')}
        </span>
      </section>
    </div>
  );
}

function ConflictCard({
  title,
  detail,
  accent,
}: {
  title: string;
  detail: string;
  accent: string;
}) {
  return (
    <span
      className="flex gap-3 rounded-[10px] border border-s-[3px] px-[15px] py-[13px]"
      style={{
        background: 'var(--gs-panel2)',
        borderColor: 'var(--gs-line)',
        borderInlineStartColor: accent,
      }}
    >
      <span className="flex flex-col gap-1">
        <span className="text-[13.5px] font-bold" style={{ color: 'var(--gs-ink)' }}>
          {title}
        </span>
        <span className="text-[12.5px] leading-snug" style={{ color: 'var(--gs-muted)' }}>
          {detail}
        </span>
      </span>
    </span>
  );
}

function countScreensForSchedule(
  schedule: Schedule,
  groupMembersById: Map<string, string[]>,
): number {
  if (schedule.screenId) return 1;
  if (schedule.screenGroupId) {
    return groupMembersById.get(schedule.screenGroupId)?.length ?? 0;
  }
  return 0;
}
